import { useMemo, useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import UrvTable from '../components/urv-table';
import Races from '../components/races';
import ParkingPlaces from '../components/parking-places';
import Access from '../components/access';
import Incidents from '../components/incidents';
import CarsList from '../components/cars-list';
import PaymentPartners from '../components/payment-partners';
import Tariffs from '../components/tariffs';
import VehicleTypeOverrides from '../components/vehicle-type-overrides';
import BuildingInfo from '../components/building-info';
import Billing from '../components/billing';
import GorkyToggle from '../components/GorkyToggle';


const widgetsTabList = [
  {
    id: 'controls',
    translateKey: 'main_tabs_controls',
    widgetItems: [
      {
        key: 'races',
        permissionKey: 'races.visible',
        node: Races,
        size: 12,
      },
    ]
  },
  {
    id: 'payment',
    translateKey: 'main_tabs_payment',
    widgetItems: [
      {
        key: 'billing',
        translateKey: 'billing',
        permissionKey: 'billing.access',
        node: Billing,
      },
      {
        key: 'rates',
        translateKey: 'rates',
        permissionKey: 'tariff.visible',
        node: Tariffs,
      },
      {
        key: 'vehicleTypeOverrides',
        translateKey: 'vehicle_type_overrides_title',
        permissionKey: 'tariff.visible',
        node: VehicleTypeOverrides,
      },
      {
        key: 'paymentPartners',
        translateKey: 'widget_toc_partners',
        permissionKey: 'paymentPartners.visible',
        node: PaymentPartners,
      },
    ]
  },
  {
    id: 'accesses',
    translateKey: 'accesses',
    widgetItems: [
      {
        key: 'access',
        translateKey: 'accesses',
        permissionKey: 'access.visible',
        node: Access,
      },
      {
        key: 'parkingPlaces',
        translateKey: 'parking_lots',
        permissionKey: 'parkingPlaces.visible',
        node: ParkingPlaces,
      },
      {
        key: 'cars',
        translateKey: 'vehicles',
        permissionKey: 'cars.visible',
        node: CarsList,
      },
    ],
  },
  {
    id: 'rest',
    translateKey: 'main_tabs_rest',
    widgetItems: [
      {
        key: 'incidents',
        translateKey: 'incidents',
        permissionKey: 'incidents.visible',
        node: Incidents,
      },
      {
        key: 'buildingInfo',
        translateKey: 'building',
        permissionKey: 'building.details',
        node: BuildingInfo,
      },
      
      {
        key: 'urv',
        translateKey: 'urv',
        permissionKey: 'urv.visible',
        node: UrvTable,
      },
      {
        key: 'gorkySwitch',
        translateKey: 'widgets_gorky_title',
        permissionKey: 'channel.gorky_toggle',
        node: GorkyToggle,
      },
    ],
  }
];

const usePermittedWidgets = () => {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState(null);

  const permittedWidgetsByTabId = useMemo(() => (
    widgetsTabList.filter((widgetsTab) => (
      widgetsTab.widgetItems.some(widgetItem => hasPermission(widgetItem.permissionKey))
    )).reduce((acc, cur) => ({
      ...acc,
      [cur.id]: cur.widgetItems.filter(widgetItem => hasPermission(widgetItem.permissionKey)),
    }), {})
  ), [hasPermission])

  const permittedTabsList = useMemo(() => (
    widgetsTabList.filter((widgetsTab) => (
      widgetsTab.widgetItems.some(widgetItem => hasPermission(widgetItem.permissionKey))
    )).map((widgetsTab) => ({ 
      id: widgetsTab.id,
      translateKey: widgetsTab.translateKey,
    }))
  ), [hasPermission]);

  const selectedTab = permittedTabsList.some((tab) => tab.id === activeTab)
    ? activeTab
    : permittedTabsList[0]?.id ?? false;

  const activeTabWidgetsList = permittedWidgetsByTabId[selectedTab] || []

  return {
    permittedTabsList,
    activeTabWidgetsList,
    activeTab: selectedTab,
    setActiveTab
  }
}

export default usePermittedWidgets;